import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft } from "@phosphor-icons/react";
import { api, resolveImg } from "../lib/api";
import { Reveal } from "../lib/motion";
import { PrimaryButton } from "../components/ui-kit";

// Render inline **bold** within a text string
function inline(text) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) =>
    p.startsWith("**") && p.endsWith("**") ? (
      <strong key={i} className="font-semibold text-navy">
        {p.slice(2, -2)}
      </strong>
    ) : (
      <React.Fragment key={i}>{p}</React.Fragment>
    )
  );
}

function renderContent(content) {
  return content.split("\n\n").map((block, i) => {
    const trimmed = block.trim();
    // Heading: **Heading** on its own line, or "## Heading"
    if (trimmed.startsWith("## ")) {
      return (
        <h3 key={i} className="mt-10 font-heading text-2xl font-semibold text-navy">
          {trimmed.replace(/^##\s+/, "")}
        </h3>
      );
    }
    if (trimmed.startsWith("**") && trimmed.endsWith("**") && !trimmed.slice(2, -2).includes("**")) {
      return (
        <h3 key={i} className="mt-10 font-heading text-2xl font-semibold text-navy">
          {trimmed.replace(/\*\*/g, "")}
        </h3>
      );
    }
    if (/^\d+\.\s/.test(trimmed) || trimmed.startsWith("- ")) {
      const items = trimmed.split("\n");
      return (
        <ul key={i} className="mt-5 space-y-3">
          {items.map((it, j) => (
            <li key={j} className="flex gap-3 text-lg leading-relaxed text-slate-600">
              <span className="mt-2.5 h-1.5 w-1.5 shrink-0 rounded-full bg-gold" />
              <span>{inline(it.replace(/^(\d+\.\s|-\s)/, ""))}</span>
            </li>
          ))}
        </ul>
      );
    }
    return (
      <p key={i} className="mt-6 text-lg leading-relaxed text-slate-600">
        {inline(trimmed)}
      </p>
    );
  });
}

export default function InsightDetail() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    setStatus("loading");
    api
      .get(`/articles/${slug}`)
      .then((res) => {
        setArticle(res.data);
        setStatus("ok");
      })
      .catch(() => setStatus("notfound"));
  }, [slug]);

  if (status === "loading") {
    return (
      <div className="grid min-h-[70vh] place-items-center pt-24">
        <div className="h-10 w-10 animate-spin rounded-full border-2 border-slate-200 border-t-navy" />
      </div>
    );
  }

  if (status === "notfound") {
    return (
      <div className="grid min-h-[70vh] place-items-center px-6 pt-24 text-center">
        <div>
          <h1 className="font-heading text-3xl font-semibold text-navy">Article not found</h1>
          <div className="mt-6 flex justify-center">
            <PrimaryButton to="/insights" testid="back-to-insights">
              Back to Insights
            </PrimaryButton>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="insight-detail-page">
      <article className="bg-white pt-36 lg:pt-44">
        <div className="mx-auto max-w-3xl px-6">
          <Reveal>
            <Link
              to="/insights"
              data-testid="insight-back-link"
              className="inline-flex items-center gap-2 text-sm font-semibold text-slate-500 transition-colors duration-300 hover:text-navy"
            >
              <ArrowLeft size={16} weight="bold" /> All Insights
            </Link>
            <div className="mt-8 text-xs font-bold uppercase tracking-[0.22em] text-teal">
              {article.category} · {article.read_time}
            </div>
            <h1 className="mt-5 font-heading text-4xl font-medium leading-tight tracking-tight text-navy sm:text-5xl">
              {article.title}
            </h1>
            <p className="mt-6 text-xl leading-relaxed text-slate-500">{article.excerpt}</p>
          </Reveal>
        </div>

        <Reveal className="mx-auto mt-12 max-w-4xl px-6">
          <div className="overflow-hidden rounded-[24px] shadow-[0_24px_60px_rgba(11,31,58,0.14)]">
            <img
              src={resolveImg(article.cover_image)}
              alt={article.title}
              className="aspect-[16/9] w-full object-cover"
            />
          </div>
        </Reveal>

        <div className="mx-auto max-w-3xl px-6 pb-8 pt-12">{renderContent(article.content)}</div>
      </article>

      <section className="bg-navy py-20 text-center lg:py-24">
        <div className="mx-auto max-w-[1320px] px-6 lg:px-10">
          <h2 className="mx-auto max-w-2xl font-heading text-3xl font-light tracking-tight text-white sm:text-4xl">
            Ready to build the company behind the idea?
          </h2>
          <div className="mt-8 flex justify-center">
            <PrimaryButton to="/contact" testid="insight-cta" className="bg-gold text-navy hover:bg-gold-light">
              Let's Build Together
            </PrimaryButton>
          </div>
        </div>
      </section>
    </div>
  );
}
